import React, { useState } from 'react';
import { ContractLine, ContractLineCreate, PriceList } from '../types';
import { BILLING_CYCLE_OPTIONS, DEFAULT_BILLING_CYCLE, ContractBillingCycleValue } from '../constants/billing';
import { ensureValidContractBillingCycle } from '../utils/billing-mappings';

interface ContractLineFormProps {
  contractId: string;
  initialData?: ContractLine | null;
  priceLists: PriceList[];
  onSubmit: (data: ContractLineCreate) => void;
  onCancel: () => void;
}

const ContractLineForm: React.FC<ContractLineFormProps> = ({
  contractId,
  initialData,
  priceLists,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<ContractLineCreate>({
    contractline_name: initialData?.contractline_name || '',
    contract_id: contractId,
    pricelist_id: initialData?.pricelist_id || 0,
    billing_cycle: ensureValidContractBillingCycle(initialData?.billing_cycle || DEFAULT_BILLING_CYCLE) as ContractBillingCycleValue,
    last_billing_start: initialData?.last_billing_start || undefined,
    last_billing_end: initialData?.last_billing_end || undefined,
    upcoming_billing_start: initialData?.upcoming_billing_start || undefined,
    upcoming_billing_end: initialData?.upcoming_billing_end || undefined,
    start: initialData?.start 
      ? new Date(initialData.start).toISOString().split('T')[0]
      : new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
    end: initialData?.end 
      ? new Date(initialData.end).toISOString().split('T')[0]
      : '',
    is_active: initialData?.is_active !== undefined ? initialData.is_active : true,
    trial_period: initialData?.trial_period || 0,
    discount_type: initialData?.discount_type || undefined,
    discount_value: initialData?.discount_value || undefined,
    tax_rate: initialData?.tax_rate || undefined,
    contractline_createdby: initialData?.contractline_createdby || 'current_user',
    isactive: initialData?.isactive !== undefined ? initialData.isactive : true,
    isdeleted: initialData?.isdeleted !== undefined ? initialData.isdeleted : false,
    iscurrent: initialData?.iscurrent !== undefined ? initialData.iscurrent : true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Use restricted options from constants
  const billingCycles = BILLING_CYCLE_OPTIONS;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    let processedValue: any = value;
    
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (type === 'number') {
      processedValue = value === '' ? undefined : Number(value);
    } else if (name === 'pricelist_id') {
      processedValue = Number(value);
    } else if (name === 'billing_cycle') {
      // Ensure billing_cycle is always in the correct format
      processedValue = ensureValidContractBillingCycle(value) as ContractBillingCycleValue;
    } else if (name === 'discount_type') {
      processedValue = value === '' ? undefined : value;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.contractline_name.trim()) {
      newErrors.contractline_name = 'Contract line name is required';
    }

    if (!formData.pricelist_id) {
      newErrors.pricelist_id = 'Price list is required';
    }

    if (!formData.start) {
      newErrors.start = 'Start date is required';
    }

    if (formData.end && formData.end <= formData.start) {
      newErrors.end = 'End date must be after start date';
    }

    if (formData.trial_period && formData.trial_period < 0) {
      newErrors.trial_period = 'Trial period cannot be negative';
    }

    if (formData.discount_type && formData.discount_value === undefined) {
      newErrors.discount_value = 'Discount value is required when discount type is selected';
    }

    if (formData.discount_type === 'percentage' && formData.discount_value !== undefined) {
      if (formData.discount_value < 0 || formData.discount_value > 100) {
        newErrors.discount_value = 'Percentage discount must be between 0 and 100';
      }
    }

    if (formData.discount_value !== undefined && formData.discount_value !== null && !formData.discount_type) {
      newErrors.discount_type = 'Discount type is required when discount value is provided';
    }

    if (!formData.contractline_createdby.trim()) {
      newErrors.contractline_createdby = 'Created by is required';
    }

    if (formData.tax_rate !== undefined && formData.tax_rate !== null) {
      if (formData.tax_rate < 0 || formData.tax_rate > 100) {
        newErrors.tax_rate = 'Tax rate must be between 0 and 100';
      }
    }

    // Validate contract ID is a valid UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!contractId || !uuidRegex.test(contractId)) {
      newErrors.contract_id = 'Invalid contract ID format';
      console.error('Invalid contract ID:', contractId);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      // Ensure contract_id is a string (it should already be from props)
      const contractIdStr = String(contractId);
      
      // Convert dates to ISO format for the backend
      const submitData: ContractLineCreate = {
        ...formData,
        contract_id: contractIdStr,
        start: formData.start ? new Date(formData.start).toISOString() : '',
        end: formData.end ? new Date(formData.end).toISOString() : undefined,
        // Ensure billing_cycle is a valid value from the allowed options
        billing_cycle: ensureValidContractBillingCycle(formData.billing_cycle || DEFAULT_BILLING_CYCLE) as ContractBillingCycleValue,
        // Ensure the backend required fields are included
        isactive: formData.isactive ?? true,
        isdeleted: formData.isdeleted ?? false,
        iscurrent: formData.iscurrent ?? true
      };
      
      // Remove empty end if not provided
      if (!submitData.end) {
        delete submitData.end;
      }
      
      console.log('Submitting contract line data:', submitData);
      console.log('Contract ID format:', contractIdStr, 'Length:', contractIdStr.length);
      console.log('Billing cycle value:', submitData.billing_cycle);
      console.log('Full submit data:', JSON.stringify(submitData, null, 2));
      onSubmit(submitData as ContractLineCreate);
    }
  };

  const getSelectedPriceList = () => {
    return priceLists.find(p => p.pricelist_id === formData.pricelist_id);
  };

  const selectedPriceList = getSelectedPriceList();

  // Filter price lists based on billing cycle compatibility
  const getCompatiblePriceLists = () => {
    const billingCycle = formData.billing_cycle || DEFAULT_BILLING_CYCLE;
    
    // Map contract line billing cycles to price list billing periods
    // The backend returns billing_period as enum values like "1_month", "3_months", "12_months"
    const cycleMapping: { [key: string]: string[] } = {
      '1_month': ['1_month'],
      '3_months': ['3_months'],
      '12_months': ['12_months']
    };

    const compatiblePeriods = cycleMapping[billingCycle] || [];
    
    return priceLists.filter(pl => 
      pl.is_active && 
      compatiblePeriods.includes(pl.billing_period)
    );
  };

  const compatiblePriceLists = getCompatiblePriceLists();

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Contract Line Name *</label>
        <input
          type="text"
          name="contractline_name"
          value={formData.contractline_name}
          onChange={handleChange}
          className={`form-control ${errors.contractline_name ? 'error' : ''}`}
          placeholder="Enter contract line name"
        />
        {errors.contractline_name && <div className="form-error">{errors.contractline_name}</div>}
      </div>

      <div className="form-group">
        <label className="form-label">Billing Cycle</label>
        <select
          name="billing_cycle"
          value={formData.billing_cycle}
          onChange={handleChange}
          className="form-control"
        >
          {billingCycles.map((cycle) => (
            <option key={cycle.value} value={cycle.value}>
              {cycle.label}
            </option>
          ))}
        </select>
        <p style={{ fontSize: '0.875em', color: '#6c757d', marginTop: '5px' }}>
          Note: Only price lists matching this billing cycle will be available
        </p>
      </div>

      <div className="form-group">
        <label className="form-label">Price List *</label>
        <select
          name="pricelist_id"
          value={formData.pricelist_id}
          onChange={handleChange}
          className={`form-control ${errors.pricelist_id ? 'error' : ''}`}
        >
          <option value={0}>Select a price list</option>
          {compatiblePriceLists.map((priceList) => (
            <option key={priceList.pricelist_id} value={priceList.pricelist_id}>
              {priceList.name} - {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: priceList.currency
              }).format(priceList.amount)} ({priceList.billing_period})
            </option>
          ))}
        </select>
        {errors.pricelist_id && <div className="form-error">{errors.pricelist_id}</div>}
        {compatiblePriceLists.length === 0 && (
          <p style={{ color: '#dc3545', fontSize: '0.875em', marginTop: '5px' }}>
            No price lists available for the selected billing cycle
          </p>
        )}
      </div>

      {selectedPriceList && (
        <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#495057' }}>Selected Price List Details</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', fontSize: '12px' }}>
            <div>
              <strong>Plan:</strong> {selectedPriceList.plan?.name || 'N/A'}
            </div>
            <div>
              <strong>Amount:</strong> {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: selectedPriceList.currency
              }).format(selectedPriceList.amount)}
            </div>
            <div>
              <strong>Billing:</strong> {selectedPriceList.billing_period}
            </div>
            <div>
              <strong>Country:</strong> {selectedPriceList.country || 'All'}
            </div>
          </div>
        </div>
      )}

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Start Date *</label>
            <input
              type="date"
              name="start"
              value={formData.start}
              onChange={handleChange}
              className={`form-control ${errors.start ? 'error' : ''}`}
            />
            {errors.start && <div className="form-error">{errors.start}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">End Date</label>
            <input
              type="date"
              name="end"
              value={formData.end}
              onChange={handleChange}
              className={`form-control ${errors.end ? 'error' : ''}`}
            />
            {errors.end && <div className="form-error">{errors.end}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Trial Period (Days)</label>
            <input
              type="number"
              name="trial_period"
              value={formData.trial_period}
              onChange={handleChange}
              className={`form-control ${errors.trial_period ? 'error' : ''}`}
              min="0"
              placeholder="0"
            />
            {errors.trial_period && <div className="form-error">{errors.trial_period}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Created By *</label>
            <input
              type="text"
              name="contractline_createdby"
              value={formData.contractline_createdby}
              onChange={handleChange}
              className={`form-control ${errors.contractline_createdby ? 'error' : ''}`}
              placeholder="Enter creator name"
            />
            {errors.contractline_createdby && <div className="form-error">{errors.contractline_createdby}</div>}
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Discount Type</label>
            <select
              name="discount_type"
              value={formData.discount_type || ''}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">No discount</option>
              <option value="percentage">Percentage</option>
              <option value="fixed">Fixed Amount</option>
            </select>
            {errors.discount_type && <div className="form-error">{errors.discount_type}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Discount Value</label>
            <input
              type="number"
              name="discount_value"
              value={formData.discount_value !== undefined && formData.discount_value !== null ? formData.discount_value : ''}
              onChange={handleChange}
              className={`form-control ${errors.discount_value ? 'error' : ''}`}
              min="0"
              step="0.01"
              placeholder={formData.discount_type === 'percentage' ? 'Enter percentage (0-100)' : 'Enter amount'}
              disabled={!formData.discount_type}
            />
            {formData.discount_type === 'percentage' && formData.discount_value !== undefined && formData.discount_value !== null && (
              <p style={{ fontSize: '0.875em', color: '#6c757d', marginTop: '5px' }}>
                {formData.discount_value}% discount
              </p>
            )}
            {errors.discount_value && <div className="form-error">{errors.discount_value}</div>}
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Tax Rate (%)</label>
        <input
          type="number"
          name="tax_rate"
          value={formData.tax_rate !== undefined && formData.tax_rate !== null ? formData.tax_rate : ''}
          onChange={handleChange}
          className={`form-control ${errors.tax_rate ? 'error' : ''}`}
          min="0"
          max="100"
          step="0.01"
          placeholder="Enter tax rate (e.g., 19 for 19%)"
        />
        {errors.tax_rate && <div className="form-error">{errors.tax_rate}</div>}
        <p style={{ fontSize: '0.875em', color: '#6c757d', marginTop: '5px' }}>
          Common rates: 0% (tax-free), 7% (reduced), 19% (standard in Germany)
        </p>
      </div>

      <div className="form-group">
        <label className="form-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            style={{ marginRight: '8px' }}
          />
          Active
        </label>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default ContractLineForm;